import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Facility, FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';
import { UserRoles } from 'src/app/dictionaries/UserRoles';

export interface SharingPatientId {
  facility: string;
  value: string;
  ownerId: string;
}

interface MockUser {
  id: string;
  name: string;
  email: string;
  facilityId: string;
  role?: string;
}

@Component({
    selector: 'app-sharing-setup',
    templateUrl: './sharing-setup.component.html',
    styleUrls: ['./sharing-setup.component.scss'],
    standalone: false
})
export class SharingSetupComponent implements OnChanges, OnInit {
  // Start with mock users but allow dynamic additions
  private dynamicUsers: MockUser[] = [];
  availableUsers: MockUser[] = [];
  filteredUsers: MockUser[] = [];
  userSearchQuery: string = '';
  filteredFacilities: Facility[] = [];
  facilitySearchQuery: string = '';
  @Input() facilities: Facility[] = [];
  @Input() currentFacility: string | null = null;
  @Input() isImageShareMode = false;
  private _currentPage = 1;
  @Input()
  set currentPage(value: number | string) {
    // Normalize to number to ensure ngSwitch works correctly
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    this._currentPage = numValue;

    // Mark for check; parent drives change detection
    this.cdr.markForCheck();
  }
  get currentPage(): number {
    return this._currentPage;
  }
  @Input() totalPages = 0;
  @Input() currentFacilityPatientId: SharingPatientId | null = null;
  @Input() otherPatientIDs: SharingPatientId[] = [];
  @Input() selectedPatientId: SharingPatientId | null = null;
  @Input() selectedTargetFacility: SharingPatientId | null = null;
  @Input() selectedFacilityUsers: any[] = [];
  @Input() shareTypeSelected: 'document' | 'image' | null = null;
  @Input() shareDirection: 'send' | 'receive' | null = null;
  @Input() patientData: any;

  @Output() documentShare = new EventEmitter<void>();
  @Output() imageShare = new EventEmitter<void>();
  @Output() shareWithFacility = new EventEmitter<void>();
  @Output() shareFromFacility = new EventEmitter<void>();
  @Output() facilitySelected = new EventEmitter<SharingPatientId>();
  @Output() facilityUserSelected = new EventEmitter<any[]>();
  @Output() shareTypeSelectedEvent = new EventEmitter<'document' | 'image'>();
  @Output() sendSelected = new EventEmitter<void>();
  @Output() receiveSelected = new EventEmitter<void>();
  @Output() goNext = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() restartSetup = new EventEmitter<void>();
  @Output() createFacilityUser = new EventEmitter<void>();
  @Output() addTargetFacility = new EventEmitter<void>();
  @Output() editTargetFacility = new EventEmitter<void>();
  @Output() editSelectedUsers = new EventEmitter<void>();
  @Output() editShareType = new EventEmitter<void>();
  @Output() editDirection = new EventEmitter<void>();
  @Output() completeImageSend = new EventEmitter<void>();

  get stepMessage(): string {
    switch (this.currentPage) {
      case 1:
        return 'Select a target facility';
      case 2:
        return 'Select a type of exchange';
      case 3:
        return 'Select between SEND and RETRIEVE';
      case 4:
        return 'Select facility users';
      default:
        return '';
    }
  }

  get stepSubtitle(): string {
    switch (this.currentPage) {
      case 1:
        return 'Choose the destination for this patient\'s medical records exchange.';
      case 2:
        return 'Select whether to exchange images or documents.';
      case 3:
        return 'Choose to send data to the facility or retrieve data from it.';
      case 4:
        return '';
      default:
        return '';
    }
  }

  get progressSteps(): Array<{ number: number; label: string }> {
    return [
      { number: 1, label: 'Facility' },
      { number: 2, label: 'Type' },
      { number: 3, label: 'Direction' },
      { number: 4, label: 'Review' }
    ];
  }

  get shouldShowContinueButton(): boolean {
    return !!(this.selectedTargetFacility && this.currentPage === 1);
  }

  get selectedTargetFacilityName(): string {
    const facilityId = this.selectedTargetFacility?.facility;
    if (!facilityId) return '';

    const normalized = facilityId.toLowerCase().trim();
    const match = (this.facilities || []).find(f => (f?.id || '').toLowerCase().trim() === normalized);
    return match?.name || facilityId;
  }

  /**
   * Format facility name for display (convert dashes to spaces and capitalize words)
   */
  getFormattedFacilityName(facilityId: string): string {
    if (!facilityId) return '';
    // Convert dashes to spaces and capitalize each word
    return facilityId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get currentFacilityName(): string {
    const facilityId = this.currentFacilityPatientId?.facility || this.currentFacility || '';
    if (!facilityId) return '';
    // Try to find in facilities array first
    const normalized = facilityId.toLowerCase().trim();
    const match = (this.facilities || []).find(f => (f?.id || '').toLowerCase().trim() === normalized);
    if (match?.name) return match.name;
    // Fallback to formatting the ID
    return this.getFormattedFacilityName(facilityId);
  }

  get targetFacilityName(): string {
    const facilityId = this.selectedTargetFacility?.facility;
    if (!facilityId) return '';
    // Try to find in facilities array first
    const normalized = facilityId.toLowerCase().trim();
    const match = (this.facilities || []).find(f => (f?.id || '').toLowerCase().trim() === normalized);
    if (match?.name) return match.name;
    // Fallback to formatting the ID
    return this.getFormattedFacilityName(facilityId);
  }

  get showFacilityAnimation(): boolean {
    // We can show the animation even if the current facility has no patientId record,
    // as long as we know the current facility identifier and the user picked a target.
    const sourceFacility = (this.currentFacilityPatientId?.facility || this.currentFacility || '').trim();
    const targetFacility = (this.selectedTargetFacility?.facility || '').trim();
    return !!(sourceFacility && targetFacility);
  }

  get selectedFacilityUsersDisplay(): Array<{ id?: string; label: string }> {
    const users = Array.isArray(this.selectedFacilityUsers) ? this.selectedFacilityUsers : [];
    return users.filter(Boolean).map((u: any) => ({
      id: u?.id,
      label: (u?.name || u?.email || u?.id || 'User') as string
    }));
  }

  trackBySelectedUserChip(index: number, u: { id?: string; label: string }): string {
    return (u?.id || u?.label || String(index)) as string;
  }

  constructor(
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    // Initialize filtered facilities
    this.filteredFacilities = [...(this.facilities || [])];

    // CRITICAL: If selectedTargetFacility is already set (e.g., from URL restoration),
    // preload the users for later use (they'll be needed on page 4 if image + send)
    if (this.selectedTargetFacility) {
      const facilityId = this.selectedTargetFacility.facility?.toLowerCase().trim();
      const hasUsersForFacility = this.dynamicUsers.some(u => u.facilityId.toLowerCase() === facilityId);

      if (!hasUsersForFacility && (this.currentPage === 4 || this.currentPage === 1)) {
        await this.getAvailableUsers(this.selectedTargetFacility);
        // Update available users after loading (only if on page 4)
        if (this.currentPage === 4) {
          this.updateAvailableUsers();
        }
      }
    }

    // Trigger change detection after initialization to ensure template is updated
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
  }

  // Step 1: Select target facility
  async onFacilitySelected(patientId: SharingPatientId): Promise<void> {
    this.facilitySelected.emit(patientId);
    // Auto-advance is handled in facade

    // Lets update the available users list
    await this.getAvailableUsers(patientId);
  }

  async getAvailableUsers(patientId: SharingPatientId): Promise<void> {
    const facilityId = patientId.facility.toLowerCase().trim();
    const users = await this.firebaseUtilities_$.getFacilityUsers(facilityId);

    const mappedUsers: MockUser[] = users.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      facilityId: facilityId,
      role: user.role || 'Facilitator'
    }));

    // Add to dynamicUsers to maintain the source of truth
    // Remove existing users for this facility first to avoid duplicates
    this.dynamicUsers = this.dynamicUsers.filter(u => u.facilityId.toLowerCase() !== facilityId.toLowerCase());
    this.dynamicUsers.push(...mappedUsers);

    // Update available users
    this.availableUsers = mappedUsers;
    // Update filtered users
    this.filteredUsers = [...mappedUsers];
  }

  // Step 2: Toggle facility user selection (multiple selection)
  onFacilityUserSelected(user: MockUser): void {
    // Ensure we have a valid user with an ID
    if (!user || !user.id) {
      console.warn('Invalid user selected:', user);
      return;
    }

    // Create a new array to avoid mutation issues
    // Don't mutate the @Input() directly - let the parent handle state
    let updatedSelection: MockUser[];
    const index = this.selectedFacilityUsers.findIndex(u => u && u.id === user.id);

    if (index >= 0) {
      // User already selected, remove from selection
      updatedSelection = this.selectedFacilityUsers.filter(u => u && u.id !== user.id);
    } else {
      // User not selected, add to selection
      updatedSelection = [...this.selectedFacilityUsers, user];
    }

    // Emit the updated array - parent will update the @Input()
    this.facilityUserSelected.emit(updatedSelection);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['facilities']) {
      // Update filtered facilities when facilities input changes
      this.filteredFacilities = [...(this.facilities || [])];
      // Re-apply search filter if there's an active search query
      if (this.facilitySearchQuery) {
        this.onFacilitySearchChange();
      }
    }

    if (changes['selectedTargetFacility']) {
      // CRITICAL: When selectedTargetFacility is set (from URL restoration or user click),
      // preload the users from Firebase for later use (they'll be needed on page 4 if image + send)
      if (changes['selectedTargetFacility'].currentValue && !changes['selectedTargetFacility'].firstChange) {
        const facility = changes['selectedTargetFacility'].currentValue;
        // Check if users for this facility are already loaded
        const facilityId = facility.facility?.toLowerCase().trim();
        const hasUsersForFacility = this.dynamicUsers.some(u => u.facilityId.toLowerCase() === facilityId);

        if (!hasUsersForFacility) {
          await this.getAvailableUsers(facility);
          // Update available users AFTER loading (only if on page 4)
          if (this.currentPage === 4) {
            this.updateAvailableUsers();
          }
        } else if (this.currentPage === 4) {
          // Users already loaded, just update the list if on page 4
          this.updateAvailableUsers();
        }
      }
    }

    if (changes['currentPage']) {
      // CRITICAL: When navigating to page 4, ensure users are loaded for the selected facility
      // Page 4 is now the users selection page (only shown for image + send)
      if (changes['currentPage'].currentValue === 4 && this.selectedTargetFacility) {
        const facilityId = this.selectedTargetFacility.facility?.toLowerCase().trim();
        const hasUsersForFacility = this.dynamicUsers.some(u => u.facilityId.toLowerCase() === facilityId);

        if (!hasUsersForFacility) {
          await this.getAvailableUsers(this.selectedTargetFacility);
          // Update available users AFTER loading
          this.updateAvailableUsers();
        } else {
          // Users already loaded, just update the list
          this.updateAvailableUsers();
        }
      } else if (changes['currentPage'].currentValue !== 4) {
        // Not on page 4, update available users (will return early if not page 4)
        this.updateAvailableUsers();
      }
    }

    // Update available users when target facility changes (but not if we already handled it above)
    if (changes['selectedTargetFacility'] && (!changes['currentPage'] || changes['currentPage'].currentValue !== 4)) {
      this.updateAvailableUsers();
    }

    // Trigger change detection when inputs change
    // This ensures the template updates when selectedTargetFacility or currentPage changes
    if (changes['selectedTargetFacility'] || changes['currentPage']) {
      this.cdr.markForCheck();
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
  }

  updateAvailableUsers(): void {
    if (this.currentPage !== 4) {
      // Don't clear availableUsers if we're not on page 4, just return
      // This preserves the users that were loaded from Firebase
      return;
    }

    // Filter users by the TARGET facility (selected in step 1), not the source facility
    // Use selectedTargetFacility to get the target facility information
    if (!this.selectedTargetFacility) {
      // If no target facility selected yet, show empty list
      this.availableUsers = [];
      this.filteredUsers = [];
      this.userSearchQuery = '';
      return;
    }

    // Get facility name from the target facility (selected in step 1)
    const facilityName = this.selectedTargetFacility.facility.toLowerCase().trim();
    const facilityValue = this.selectedTargetFacility.facility.toLowerCase().trim();
    const facilityId = facilityValue.toLowerCase().trim();

    // If not found, extract facility prefix from patient ID (e.g., "ehh" from "ehh-dc-001")
    // Handle formats like "ZSH-DC-001", "zsh-dc-001", "DC0001", etc.
    let facilityPrefix = facilityValue.split('-')[0].toLowerCase();

    // Handle DC0001 format - it's John Dell Hospital
    if (facilityValue.match(/^dc\d+$/i)) {
      facilityPrefix = 'dc';
    }

    if (facilityId) {
      // Filter users by exact facility ID match and only show Facility users (CLIENT_ADMIN role)
      // CRITICAL: UserRoles.admin is 'CLIENT_ADMIN'
      // The UI displays 'Facility user' but the actual role value is 'CLIENT_ADMIN'
      this.availableUsers = this.dynamicUsers.filter(user => {
        const userFacilityIdNormalized = user.facilityId?.toLowerCase().trim();
        const targetFacilityIdNormalized = facilityId.toLowerCase().trim();
        const facilityMatches = userFacilityIdNormalized === targetFacilityIdNormalized;
        const roleMatches = user.role === UserRoles.admin || user.role === 'CLIENT_ADMIN';
        return facilityMatches && roleMatches;
      });
      // Update filtered users
      this.filteredUsers = [...this.availableUsers];
      // Reset search query
      this.userSearchQuery = '';
    } else {
      // If no match found, try fuzzy matching and only show Facilitators
      const fuzzyMatched = this.dynamicUsers.filter(user => {
        return (
          (this.facilityMatches(user.facilityId, facilityName) ||
            this.facilityMatches(user.facilityId, facilityValue)) &&
          user.role === 'Facilitator'
        );
      });

      // Only use fuzzy matched results if found, otherwise show empty list
      // This prevents showing wrong users when facility can't be identified
      this.availableUsers = fuzzyMatched;
      // Update filtered users
      this.filteredUsers = [...this.availableUsers];
      // Reset search query
      this.userSearchQuery = '';

      // Log warning if no users found for debugging
      if (this.availableUsers.length === 0) {
        console.warn('No users found for target facility:', {
          facilityName,
          facilityValue,
          selectedTargetFacility: this.selectedTargetFacility,
          dynamicUsersCount: this.dynamicUsers.length
        });
      }
    }
  }

  private facilityMatches(userFacilityId: string, facilityName: string): boolean {
    // Normalize and match facility names
    const normalizedFacilityName = this.normalizeFacilityName(facilityName);

    // Extract facility prefix from ID (e.g., 'jdh' from 'jdh-dc-001')
    const facilityPrefix = userFacilityId.split('-')[0].toLowerCase();

    // Check if facility name contains the prefix or vice versa
    // Also check if any part of the facility name matches
    const nameWords = normalizedFacilityName.split('-');
    return (
      normalizedFacilityName.includes(facilityPrefix) ||
      facilityPrefix.includes(normalizedFacilityName.substring(0, 3)) ||
      nameWords.some(word => word.startsWith(facilityPrefix.substring(0, 2))) ||
      facilityPrefix.includes(nameWords[0]?.substring(0, 2) || '')
    );
  }

  private normalizeFacilityName(name: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  isUserSelected(user: MockUser): boolean {
    if (!user || !user.id) {
      return false;
    }
    // Strict comparison to ensure only the exact user is selected
    return this.selectedFacilityUsers.some(u => u && u.id === user.id);
  }

  trackByUserId(_index: number, user: MockUser): string {
    return user.id;
  }

  /**
   * Get the display label for a user role
   */
  getRoleLabel(role: string | undefined): string {
    if (!role) return '';
    if (role === 'CLIENT_ADMIN' || role === UserRoles.admin) {
      return 'Facility user';
    }
    return role;
  }

  /**
   * Get user initials for avatar
   */
  getUserInitials(user: MockUser): string {
    if (!user || !user.name) return '?';
    const names = user.name.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  }

  /**
   * Handle user search query change
   */
  onUserSearchChange(): void {
    if (!this.userSearchQuery || this.userSearchQuery.trim() === '') {
      this.filteredUsers = [...this.availableUsers];
      return;
    }

    const query = this.userSearchQuery.toLowerCase().trim();
    this.filteredUsers = this.availableUsers.filter(user => {
      const nameMatch = user.name?.toLowerCase().includes(query);
      const emailMatch = user.email?.toLowerCase().includes(query);
      const roleMatch = this.getRoleLabel(user.role)?.toLowerCase().includes(query);
      return nameMatch || emailMatch || roleMatch;
    });
  }

  /**
   * Handle facility search query change
   */
  onFacilitySearchChange(): void {
    if (!this.facilitySearchQuery || this.facilitySearchQuery.trim() === '') {
      this.filteredFacilities = [...(this.facilities || [])];
      return;
    }

    const query = this.facilitySearchQuery.toLowerCase().trim();
    this.filteredFacilities = (this.facilities || []).filter(facility => {
      const nameMatch = facility.name?.toLowerCase().includes(query);
      const idMatch = facility.id?.toLowerCase().includes(query);
      const addressMatch = facility.address?.toLowerCase().includes(query);
      return nameMatch || idMatch || addressMatch;
    });
  }

  trackByFacilityId(_index: number, facility: Facility): string {
    return facility.id || '';
  }

  /**
   * Handle cancel exchange
   */
  onCancelExchange(): void {
    this.restartSetup.emit();
  }

  /**
   * Handle go back
   */
  onGoBack(): void {
    if (this.currentPage > 1) {
      this.pageChange.emit(this.currentPage - 1);
    }
  }

  // Step 2: Select share type (moved from step 3)
  onDocumentShare(): void {
    this.shareTypeSelectedEvent.emit('document');
    this.documentShare.emit();
  }

  onImageShare(): void {
    this.shareTypeSelectedEvent.emit('image');
    this.imageShare.emit();
  }

  // Step 3: Select SEND or RECEIVE (moved from step 4)
  onSendSelected(): void {
    this.sendSelected.emit();
    console.log('-- onSendSelected');
    // Auto-advance is handled in facade
  }

  onReceiveSelected(): void {
    this.receiveSelected.emit();
    // Auto-advance is handled in facade
  }

  // Legacy methods for backward compatibility
  onShareWithFacility(): void {
    this.shareWithFacility.emit();
    this.goToNextStep();
  }

  onShareFromFacility(): void {
    this.shareFromFacility.emit();
    this.goToNextStep();
  }

  onGoNext(): void {
    this.goNext.emit();
  }

  onRestartSetup(): void {
    this.restartSetup.emit();
  }

  /**
   * Add a newly created user to the dynamic users list
   */
  addNewUser(user: { id: string; name: string; email: string; role?: string; facility?: string }): void {
    // Determine facility ID from the selected target facility
    const facilityId = this.selectedTargetFacility?.value || '';

    const newUser: MockUser = {
      id: user.id,
      name: user.name,
      email: user.email,
      facilityId: facilityId,
      role: user.role || 'Facilitator'
    };

    // Add to dynamic users list
    this.dynamicUsers.push(newUser);

    // Refresh available users to show the newly created user
    this.updateAvailableUsers();
  }

  onCreateFacilityUser(): void {
    this.createFacilityUser.emit();
  }

  onAddTargetFacility(): void {
    this.addTargetFacility.emit();
  }

  trackByPatientId(_index: number, item: SharingPatientId): string {
    return `${item.facility}_${item.value}`;
  }

  isSelected(patientId: SharingPatientId): boolean {
    const selectedId = this.selectedTargetFacility || this.selectedPatientId;
    if (!selectedId || !patientId) {
      return false;
    }
    // Case-insensitive comparison for facility IDs
    const selectedFacility = selectedId.facility?.toLowerCase().trim();
    const patientFacility = patientId.facility?.toLowerCase().trim();
    return selectedFacility === patientFacility && selectedId.value === patientId.value;
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onContinueToNextStep(): void {
    // This method is now used for page 4 (users selection)
    // Only allow continuing if at least one user is selected
    if (this.selectedFacilityUsers.length > 0) {
      // After selecting users, complete the image send flow
      // Only emit if we're in image + send mode
      if (this.shareTypeSelected === 'image' && this.shareDirection === 'send') {
        this.completeImageSend.emit();
      }
    }
  }

  onContinueFromStep1(): void {
    // Continue from step 1 to step 2 after facility is selected
    if (this.selectedTargetFacility) {
      this.onPageChange(2);
    } else {
      console.warn('SharingSetupComponent: Cannot continue - no facility selected');
    }
  }

  private goToNextStep(): void {
    if (this.currentPage >= this.totalPages) {
      return;
    }

    const nextPage = this.currentPage + 1;
    this.pageChange.emit(nextPage);
  }

  onEditTargetFacility(): void {
    this.editTargetFacility.emit();
  }

  onEditSelectedUsers(): void {
    this.editSelectedUsers.emit();
  }

  onEditShareType(): void {
    this.editShareType.emit();
  }

  onEditDirection(): void {
    this.editDirection.emit();
  }

  /**
   * Creates enhanced patient data that includes sharing setup values
   * This ensures the Chrome extension receives all the setup information
   * (origin facility, target facility, selected users, share type, etc.)
   */
  getEnhancedPatientData(): any {
    if (!this.patientData) {
      return null;
    }

    // Create a clean object with patient data and sharing setup values
    const enhancedData = {
      ...this.patientData,
      // Include sharing setup values
      sharingSetup: {
        originFacility: this.currentFacilityPatientId,
        targetFacility: this.selectedTargetFacility,
        selectedUsers:
          this.selectedFacilityUsers?.map((u: any) => ({
            id: u?.id,
            name: u?.name,
            email: u?.email,
            facilityId: u?.facilityId,
            role: u?.role
          })) || [],
        shareType: this.shareTypeSelected,
        shareDirection: this.shareDirection,
        currentFacility: this.currentFacility
      }
    };

    return enhancedData;
  }
}
